/**
 * Bob Laadt Op — bob-ticker (golvende USP-band; de rechte variant
 * .ticker--flat / data-flat heeft geen script nodig).
 *
 * Het lint staat stil; de letters rijden erdoorheen. Per letter zetten we de
 * fase van de golf-animatie op basis van zijn x in de track, zodat de golf
 * visueel op zijn plek blijft liggen. Zie de toelichting in bob-ticker.css.
 * Bron: _design/Bob-laadt-op-thomas/index.html r. 3587–3684 (1-op-1), als
 * custom element zodat het na een re-render in de theme editor opnieuw werkt.
 */

/* MOET gelijk blijven aan het masker in de CSS (.ticker::before) */
const PERIOD_TARGET = 420;

class BobTicker extends HTMLElement {
  connectedCallback() {
    /* Rechte band (setting "Golvende band" uit): alleen doorschuiven via CSS,
       geen letters knippen en geen fase per letter. */
    if (this.hasAttribute('data-flat')) return;

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.track = /** @type {HTMLElement|null} */ (this.querySelector('.ticker-track'));
    this.seqs = Array.from(this.querySelectorAll('.ticker-seq'));
    if (!this.track || !this.seqs.length) return;

    this.noMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    /** @type {HTMLElement[]} */
    this.chars = [];

    this.split();
    this.place();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.place();
      });
    }

    let raf = 0;
    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.place());
      },
      { signal }
    );
    this.noMotion.addEventListener('change', () => this.place(), { signal });
    this.watchVisibility();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
  }

  /* Tekst in losse tekens knippen. Daarmee zijn de reeksen onleesbaar voor
     een screenreader, dus die krijgen aria-hidden; de leesbare tekst staat in
     de .visually-hidden alinea erboven. Al geknipt (element opnieuw in de DOM
     gehangen)? Dan niet nog eens. */
  split() {
    this.seqs.forEach((seq) => seq.setAttribute('aria-hidden', 'true'));
    this.querySelectorAll('.ticker-msg').forEach((msg) => {
      if (msg.hasAttribute('data-split')) return;
      const text = msg.textContent || '';
      msg.textContent = '';
      for (const ch of text) {
        const span = document.createElement('span');
        span.className = 'ticker-ch';
        span.textContent = ch === ' ' ? ' ' : ch;
        msg.appendChild(span);
      }
      msg.setAttribute('data-split', '');
    });
    this.chars = Array.from(this.querySelectorAll('.ticker-ch, .ticker-bolt'));
  }

  place() {
    const track = this.track;
    if (!track) return;

    if (this.noMotion.matches) {
      this.chars.forEach((el) => {
        el.style.animationDuration = '';
        el.style.animationDelay = '';
      });
      return;
    }
    const seqWidth = this.seqs[0].getBoundingClientRect().width;
    if (!seqWidth) return;

    /* Heel aantal golven per reeks, anders springt de golf op het moment
       dat de loop rondgaat. */
    const waves = Math.max(1, Math.round(seqWidth / PERIOD_TARGET));
    const period = seqWidth / waves;
    this.style.setProperty('--tk-period', period + 'px');

    /* De band legt seqWidth px af in scrollDur, dus v = seqWidth/scrollDur.
       Eén golf duurt period/v = scrollDur/waves — een deler van de
       loopduur, dus de fases blijven gekoppeld. */
    const scrollDur = parseFloat(getComputedStyle(track).animationDuration) || 38;
    const waveDur = scrollDur / waves;
    const v = seqWidth / scrollDur;

    /* Fase per letter. De letter zit op x in de track en schuift naar links,
       dus zijn schermpositie is X(t) = x - v*t. De golf moet dan
       A*sin(2*pi*X(t)/periode) zijn. Uitgewerkt tegen de keyframes levert
       dat een delay van +x/v op. Een positieve delay zou betekenen dat de
       animatie nog niet loopt (letter blijft plat), dus we halen er hele
       golfperiodes af: dezelfde fase, maar direct actief. */
    const trackLeft = track.getBoundingClientRect().left;
    /* Eerst alles meten, dan pas schrijven. Om en om herberekent de browser
       bij elke letter de hele stijl: op een telefoon bijna een seconde vast. */
    const xs = this.chars.map((el) => {
      const r = el.getBoundingClientRect();
      return r.left - trackLeft + r.width / 2;
    });
    this.chars.forEach((el, i) => {
      const faseSec = (((xs[i] / v) % waveDur) + waveDur) % waveDur; /* 0..waveDur */
      el.style.animationDuration = waveDur.toFixed(4) + 's';
      el.style.animationDelay = (faseSec - waveDur).toFixed(4) + 's';
    });

    /* De letters worden door dit script aangemaakt, dus hun animatie start
       later dan die van de band. Dat is een constante faseverschuiving —
       zonder deze correctie ligt de tekst niet op de hartlijn van het lint,
       en hoeveel hij ernaast ligt hangt af van hoe snel de fonts laden.
       Beide tijdassen op hetzelfde nulpunt zetten maakt het exact. */
    if (typeof track.getAnimations === 'function') {
      /* In één keer ophalen: getAnimations() per letter kost op een telefoon
         honderden ms, met subtree 1 ms. */
      const all = track.getAnimations({ subtree: true });
      const scroll = all.find((a) => a.effect?.target === track);
      if (scroll) {
        /* Staat de band stil buiten beeld, dan is startTime leeg en loopt
           het via currentTime. */
        const { startTime, currentTime } = scroll;
        for (const a of all) {
          if (a.animationName !== 'ticker-wave') continue;
          if (startTime !== null) a.startTime = startTime;
          else a.currentTime = currentTime;
        }
      }
    }
  }

  /* Honderden letters animeren voor niemand zolang de band buiten beeld is. */
  watchVisibility() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      ([entry]) => this.toggleAttribute('data-paused', !entry.isIntersecting),
      { rootMargin: '100px 0px' }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('bob-ticker')) customElements.define('bob-ticker', BobTicker);
